import React from 'react'
import { Navigate, RouteObject } from 'react-router-dom'
import { PortalNavigate } from './PortalNavigate'
import { LayoutAdmin } from '../layouts/Admin/Admin'
import { RouteRoleGuard } from '../layouts/RouteRoleGuard/RouteRoleGuard'
import MyProfile from '../pages/profile/MyProfile'
import BrandSettingsResetPassword from '../pages/brand/settings/ResetPassword'
import BrandSettings from '../pages/brand/settings/Settings'
import Onboarding from '../pages/brand/settings/onboarding/Onboarding'
import TutorialsList from '../pages/tutorials/TutorialsList'
import { BrandTeamCreateProjectDistribution, ExpertProfilePath, BrandProPaths } from '../components/Header/configuration/navs'
import { NetworkRouteChildren } from './networkRoutes'
import { ExpertsV2Listing } from '../pages/brand/expertsV2/Experts'
import { VideoListingV2 } from '../pages/admin/videos/VideosV2'
import { VideoUGC } from '../pages/admin/videos/UGC/video'
import { VideoXGC } from '../pages/admin/videos/XGC/video'
import { VideoTDCV2 } from '../pages/admin/videos/TDC/videoV2'
import { EmbedV2Listing } from '../pages/admin/embeds/embedsV2'
import VideoProduction from '../pages/videoProduction/VideoProduction'
import ExpertSelection from '../pages/videoProduction/expertSelection'
import OrdersPage from '../pages/orders'
import Dashboard from '../pages/dashboard'

// BRAND_TEAM_SALES ('brand sales') gets the main-portal shell with the SAME
// page surface as BRAND_ADMIN / BRAND_MEMBER (sidebar parity — see
// brandNav.tsx): Dashboard, Catalog, Intelligence, Opportunities, Production,
// Content and Experts. Only Settings stays minimal (Profile / Reset Password
// — filtered in Settings.jsx). Backend authorization still decides what each
// endpoint serves the role. Replaced the old redirect-only router that
// forwarded into the RMN white-label portal (TDC-1499); the brand-portal
// workspace (/portal/:brandSlug/*, old /rmn/* links shimmed) still mounts
// the same pages via default.tsx.
export const BrandSalesRoutes: RouteObject[] = [
  {
    path: '/',
    element: (
      <RouteRoleGuard roles={['BRAND_TEAM_SALES']}>
        <LayoutAdmin />
      </RouteRoleGuard>
    ),
    children: [
      ...BrandProPaths,
      ...ExpertProfilePath,
      {
        path: '/experts/featured',
        element: <ExpertsV2Listing />,
      },
      ...BrandTeamCreateProjectDistribution,
      ...NetworkRouteChildren,
      {
        path: '',
        index: true,
        element: <PortalNavigate to="/dashboard" />,
      },
      {
        path: 'dashboard',
        element: <Dashboard />,
      },
      {
        path: '/videos',
        element: <VideoListingV2 />,
        children: [
          {
            path: '',
            element: <PortalNavigate to="/videos/tdc" />,
          },
          {
            path: 'ugc',
            element: <VideoUGC />,
          },
          {
            path: 'xgc',
            element: <VideoXGC />,
          },
          {
            path: 'tdc',
            element: <VideoTDCV2 />,
          },
        ],
      },
      {
        path: '/video-production/projects',
        element: <VideoProduction />,
      },
      {
        path: '/video-production/orders',
        element: <OrdersPage />,
      },
      {
        path: '/video-production/projects/:projectId',
        element: <ExpertSelection />,
      },
      {
        path: '/videos/embeds',
        element: <EmbedV2Listing />,
      },
      {
        path: '/settings/*',
        element: <BrandSettings />,
        children: [
          {
            // This role's only brand-scoped tab is Onboarding, so the
            // portal's /settings is a doorway back to the personal ones.
            path: '',
            element: <Navigate to="/settings/profile" replace />,
          },
          {
            path: 'profile',
            // Personal tab — canonical at the root.
            element: <Navigate to="/settings/profile" replace />,
          },
          {
            path: 'reset-password',
            // Personal tab — canonical at the root.
            element: <Navigate to="/settings/reset-password" replace />,
          },
          {
            path: 'sign-in-methods',
            // Personal tab — canonical at the root.
            element: <Navigate to="/settings/sign-in-methods" replace />,
          },
          {
            // The one brand-scoped tab this role has: install snippets +
            // check (every brand account).
            path: 'onboarding',
            element: <Onboarding />,
          },
          {
            path: 'google-tag',
            element: <Navigate to="../onboarding" replace />,
          },
          {
            path: '*',
            element: <Navigate to="/404" />,
          },
        ],
      },
      {
        path: 'tutorials',
        element: <TutorialsList />,
      },
      {
        path: '*',
        element: <Navigate to="/404" replace />,
      },
    ],
  },
]
